// Typed client for /api/ui — shapes mirror the pydantic response models in
// src/brain/api/ui/*.py. Base URL is relative in dev (proxied by Vite to
// localhost:8000) and the real APIM URL in deployed builds (VITE_API_BASE_URL).
export const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? ""

export type OkfStatus = "active" | "retired"

export type SourceRef = { uri: string; label: string | null }

export type OkfRecord = {
  okf: number
  id: string
  title: string
  audience: string
  status: OkfStatus
  tags: string[]
  source_refs: SourceRef[]
  created_at: string
  created_by: string
  updated_at: string
  updated_by: string
  version: number
  content: string
}

export type OkfSummary = {
  id: string
  title: string
  audience: string
  status: OkfStatus
  tags: string[]
  updated_at: string
  updated_by: string
}

export type OkfHistoryEntry = { version: number; modified_at: string }

export type AudienceInfo = { id: string; can_write: boolean }

export type DependencyStatus = { ok: boolean; detail: string | null }

// Per-source indexer state for the pipelines we own (ADR 0025). `push` is a
// source the app writes documents into rather than crawling (tl;dv, #178) — it
// has no indexer by design, so it is reported by document count and never aged;
// `not_applicable` is a managed source with no indexer of ours; `unknown` is a
// read we could not complete, and always carries its reason.
export type IndexerVerdict =
  | "healthy"
  | "stale"
  | "failing"
  | "unknown"
  | "push"
  | "not_applicable"

export type IndexerHealth = {
  source: string
  knowledge_source: string
  kind: string
  indexer: string | null
  schedule_interval: string | null
  verdict: IndexerVerdict
  reason: string | null
  status: string | null
  last_run_status: string | null
  items_processed: number | null
  items_failed: number | null
  start_time: string | null
  end_time: string | null
  last_success_at: string | null
  stale_after_seconds: number | null
  error_message: string | null
  // Push-populated sources only: there is no run to age, so size is the signal.
  document_count: number | null
}

export type UiHealth = {
  entra: DependencyStatus
  foundry: DependencyStatus
  okf_blob: DependencyStatus
  graph_mcp: DependencyStatus
  audience_counts: Record<string, number>
  recent_activity: number
  // Worst verdict first, so a failing pipeline is never below the fold.
  indexers: IndexerHealth[]
  corpus_verdict: IndexerVerdict
}

// Unified search result item shape (per amber-harbor merge to main).
// Each result from a source has source info, content, metadata, and an optional deep-link URL.
export type SearchResultItem = {
  source: string
  id: string
  topic_key: string | null
  title: string | null
  url: string | null
  // null for the per-user legs (Graph, personal): they carry their own ranking
  // positionally and never a fabricated reranker number (ADR 0017/0021).
  score: number | null
  content: string
  // OKF results are enriched with the read-time trust view + audience (ADR
  // 0011); provisional OKF also carries added_by. Absent on non-OKF sources.
  trust: TrustView | null
  audience?: string
  added_by?: string
  // source === "personal": the caller's own note, rendered as theirs (never a
  // trust bucket, never "unconfirmed").
  your_note?: boolean
  saved_at?: string
  // type === "entity": a COLLECTION, not a claim (ADR 0024). It carries no trust,
  // so without this marker it is indistinguishable from a hit whose trust lookup
  // failed. It orients; it never evidences.
  collection?: CollectionView | null
  // Every OKF hit says which kind of record it is.
  type?: string
}

export type SearchResult = {
  query: string
  scope: string
  brain: string
  results: Record<string, unknown>
  errors: Record<string, string>
  partial: boolean
}

export type TrustView = {
  confidence: number
  bucket: "high" | "medium" | "low"
  review_by: string | null
  unverified: boolean
  premise_changed: boolean
  provisional: boolean
}

// Personal records carry this INSTEAD of a TrustView (ADR 0021): an audience of
// one has no independent confirmer, so provisional/confidence cannot mean
// anything there. Freshness still does.
export type NoteView = {
  your_note: boolean
  saved_at: string
  unverified: boolean
  review_by: string | null
}

// An `entity` record carries this INSTEAD of a TrustView (ADR 0024): a collection
// is not a claim, so there is no single statement to attest to and nothing for a
// confidence number to be about. Deliberately carries no confidence/bucket/
// provisional field -- the same discipline as NoteView above.
export type CollectionView = {
  is_collection: boolean
  last_update: string | null
}

// Mirrors StoredOkf (src/brain/okf/models.py) -- the console record endpoints
// (get/create/edit/verify) return this shape directly in the body (ADR 0011),
// not a bare OkfRecord.
export type RecordWithEtag = {
  record: OkfRecord
  etag: string
  trust: TrustView | null
  note?: NoteView | null
  collection?: CollectionView | null
}

// Steward maintenance digest — mirrors src/brain/okf/maintenance.py.
export type CohortKey =
  | "due_for_review"
  | "low_confidence"
  | "unsourced"
  | "retired_without_successor"
  | "duplicate_candidates"
  | "premise_changed"

export type MaintenanceItem = {
  id: string
  title: string
  status: OkfStatus
  confidence: number
  unverified: boolean
  updated_at: string
}

export type Cohort = {
  key: CohortKey
  count: number
  surfaced: boolean
  items: MaintenanceItem[]
}

export type RevertTarget = {
  record_id: string
  restore_version: number
}

export type JanitorLogEntry = {
  action_id: string
  phase: string
  action: string
  audience: string
  targets: string[]
  rationale: string
  performed_by: string
  performed_at: string
  revert: RevertTarget[]
}

export type JanitorActionCohort = {
  count: number
  surfaced: boolean
  items: JanitorLogEntry[]
}

export type RevertActionResult = {
  succeeded: RecordWithEtag[]
  failed: { id: string; reason: string }[]
}

export type MaintenanceReport = {
  audience: string
  threshold: number
  cohorts: Cohort[]
  janitor_actions?: JanitorActionCohort
}

export type SweepResult = {
  audience: string
  considered: number
  verified: string[]
  failed: { id: string; reason: string }[]
}

// A "run pass" comes back in one of two shapes, and the backend says which in
// `type`: the full phased janitor pass when a reasoning model is configured
// (ADR 0009 J1), or the model-free re-verify sweep when it is not. Discriminate
// on `type` — reading the sweep's fields off a pass result throws, which is
// exactly what happened once BRAIN_MODEL_* went live.
export type PlannedAction = {
  phase: string
  action: string
  record_id: string
  rationale: string
  member_ids: string[]
  merged_content: string | null
}

export type JanitorPhase = {
  applied: string[]
  paused: PlannedAction[]
  failures: { id: string; reason: string }[]
}

export type JanitorPassResult = {
  audience: string
  phases: Record<string, JanitorPhase>
}

export type RunPassResult =
  | ({ type: "janitor_pass" } & JanitorPassResult)
  | ({ type: "reverify_sweep" } & SweepResult)

// Shared fetch handler: builds headers, executes request, extracts error detail if needed.
// Returns response and errorDetail (if response not ok). Prevents inconsistent error
// message formatting between request() and requestWithEtag().
async function fetchWithErrorHandling(
  path: string,
  token: string,
  init?: RequestInit
): Promise<{ response: Response; errorDetail?: string }> {
  const r = await fetch(`${apiBaseUrl}${path}`, {
    ...init,
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  })

  if (!r.ok) {
    let detail: string
    try {
      const body = await r.json()
      detail = typeof body?.detail === "string" ? body.detail : JSON.stringify(body)
    } catch {
      detail = await r.text().catch(() => "")
    }
    return { response: r, errorDetail: detail }
  }

  return { response: r }
}

async function request<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  const { response: r, errorDetail } = await fetchWithErrorHandling(path, token, init)

  if (!r.ok) {
    const error = new Error(
      `${r.status} ${r.statusText}${errorDetail ? ` — ${errorDetail.slice(0, 200)}` : ""}`
    )
    ;(error as Error & { status?: number }).status = r.status
    throw error
  }

  return (await r.json()) as T
}

async function requestWithEtag(
  path: string,
  token: string,
  init?: RequestInit
): Promise<RecordWithEtag> {
  const { response: r, errorDetail } = await fetchWithErrorHandling(path, token, init)

  if (!r.ok) {
    const error = new Error(
      `${r.status} ${r.statusText}${errorDetail ? ` — ${errorDetail.slice(0, 200)}` : ""}`
    )
    ;(error as Error & { status?: number }).status = r.status
    throw error
  }

  // The body IS {record, etag, trust} (StoredOkf) -- the ETag response header
  // duplicates the body's etag field, the body is authoritative.
  return (await r.json()) as RecordWithEtag
}

export type CreateRecordBody = {
  title: string
  content: string
  audience: string
  tags?: string[]
  source_refs?: SourceRef[]
}

export type PatchRecordBody = Partial<{
  title: string
  content: string
  tags: string[]
  source_refs: SourceRef[]
  status: OkfStatus
}>

export const api = {
  audiences: (token: string) => request<AudienceInfo[]>("/api/ui/audiences", token),
  records: (token: string, audience: string, status?: OkfStatus | "all") =>
    request<OkfSummary[]>(
      `/api/ui/audiences/${encodeURIComponent(audience)}/records${status && status !== "all" ? `?status=${status}` : ""}`,
      token
    ),
  record: (token: string, audience: string, id: string) =>
    requestWithEtag(
      `/api/ui/records/${encodeURIComponent(audience)}/${encodeURIComponent(id)}`,
      token
    ),
  createRecord: (token: string, body: CreateRecordBody) =>
    requestWithEtag("/api/ui/records", token, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  editRecord: (
    token: string,
    audience: string,
    id: string,
    patch: PatchRecordBody,
    etag: string
  ) =>
    requestWithEtag(
      `/api/ui/records/${encodeURIComponent(audience)}/${encodeURIComponent(id)}`,
      token,
      { method: "PATCH", body: JSON.stringify(patch), headers: { "If-Match": etag } }
    ),
  history: (token: string, audience: string, id: string) =>
    request<OkfHistoryEntry[]>(
      `/api/ui/records/${encodeURIComponent(audience)}/${encodeURIComponent(id)}/history`,
      token
    ),
  historyVersion: (token: string, audience: string, id: string, version: number) =>
    request<OkfRecord>(
      `/api/ui/records/${encodeURIComponent(audience)}/${encodeURIComponent(id)}/history/${version}`,
      token
    ),
  search: (token: string, q: string, scope = "broad") =>
    request<SearchResult>(
      `/api/ui/search?q=${encodeURIComponent(q)}&scope=${encodeURIComponent(scope)}`,
      token
    ),
  health: (token: string) => request<UiHealth>("/api/ui/health", token),
  maintenance: (token: string, audience: string, threshold?: number) =>
    request<MaintenanceReport>(
      `/api/ui/audiences/${encodeURIComponent(audience)}/maintenance${
        threshold != null ? `?threshold=${threshold}` : ""
      }`,
      token
    ),
  reverifySweep: (token: string, audience: string) =>
    request<SweepResult>(
      `/api/ui/audiences/${encodeURIComponent(audience)}/maintenance/reverify-sweep`,
      token,
      { method: "POST" }
    ),
  verifyRecord: (token: string, audience: string, id: string, etag: string) =>
    requestWithEtag(
      `/api/ui/records/${encodeURIComponent(audience)}/${encodeURIComponent(id)}/verify`,
      token,
      { method: "POST", headers: { "If-Match": etag } }
    ),
  revert: (token: string, audience: string, actionId: string) =>
    request<RevertActionResult>(
      `/api/ui/audiences/${encodeURIComponent(audience)}/maintenance/revert/${encodeURIComponent(actionId)}`,
      token,
      { method: "POST" }
    ),
  runPass: (token: string, audience: string) =>
    request<RunPassResult>(
      `/api/ui/audiences/${encodeURIComponent(audience)}/maintenance/run-pass`,
      token,
      { method: "POST" }
    ),
}
